import $store from '@/store'
import ColumnUtils from '@/utils/ColumnUtils'
import { TYPE_NAMES } from '@/utils/class/AdvanceSelect'
import { Charge } from '@/class/model/act/Charge'
import * as RateUtil from '@/class/cargo/model/utils/RateUtil'
import * as ChargeMeasure from '@/class/cargo/model/parameters/ChargeMeasure'
import ChargeUtils from '@/class/cargo/model/utils/ChargeUtils'
import { ChargeItem } from '@/class/model/mdm/chargeItem'
import { Message } from '@/utils/Message'
import lang from '@/lang'
import { queryPriceAirByKey } from '@/api/price/priceAir'
import { Company } from '@/class/model/mdm/Company'
import { cache } from '@/utils/cacheManager'
import { multiply } from '@/utils/commonUtil'

/**
 * PriceAir公用的工具类
 */
export class PriceAirUtil {
  constructor() {
  }

  /**
   * 运价默认生成费用项目(根据scaleCode)
   */
  static DEFAULT_SCALE_CODE_AIR='AF';

  /**
   * 战争费
   */
  static DEFAULT_SCALE_CODE_FSC='FSC';

  /**
   * 燃油附加费
   */
  static DEFAULT_SCALE_CODE_SSC='SSC';

  /**
   * 入仓费
   */
  static DEFAULT_SCALE_CODE_TC='RCF';

  /**
   * 销售补贴
   */
  static DEFAULT_SCALE_CODE_BTA='BTA';

  static serviceItemLabelFunction(data, service) {
    return ColumnUtils.resolveColumnLabel(TYPE_NAMES.basic_data_PriceServiceAir, 'code', service,
      $store.getters.language === 'zh' ? 'chineseName' : 'englishName')
  }

  /**
   * 生成应收费用
   * @param quotation
   * @param job
   * @return
   */
  static async generatingReceivableCharge(quotation, job) {
    const charges = []
    let charge = null
    if (quotation != null) {
      const quotationAirDetail = quotation.quotationAirDetail
      if (quotationAirDetail) {
        charge = await this.convertQuotationAirDetailToCharge(quotationAirDetail, job)
        if (charge != null) {
          charges.push(charge)
        }
        charge = await this.convertQuotationAirDetailFscToCharge(quotationAirDetail, job)
        if (charge != null) {
          charges.push(charge)
        }
        charge = await this.convertQuotationAirDetailSscToCharge(quotationAirDetail, job)
        if (charge != null) {
          charges.push(charge)
        }
      }
      const quotationChargeDetails = quotation.quotationChargeDetail
      if (quotationChargeDetails && quotationChargeDetails.length > 0) {
        for (const item of quotationChargeDetails) {
          charge = await this.convertChargeDetailToCharge(item, job)
          if (charge != null) {
            charges.push(charge)
          }
        }
      }
    }
    if (charges && charges.length > 0) {
      if (!job.charges || job.charges.length === 0) {
        job.charges = []
      }
      job.charges = job.charges.concat(charges)
    }
  }

  /**
   * 根据费用价格明细生成应收费用
   */
  static async convertChargeDetailToCharge(quotationChargeDetail, job) {
    const newCharge = await this.createChargeDetailCharge(quotationChargeDetail, job)
    if (newCharge == null) return null
    newCharge.receivable = 1	// 应收
    newCharge.accountId = job.customerId	// 结算单位
    newCharge.quantity = quotationChargeDetail.num	// 数量
    newCharge.unitRate = quotationChargeDetail.rate	// 单价
    newCharge.remarks = quotationChargeDetail.remarks	// 备注
    // 单价是否含税
    newCharge.unitPriceIncludeTax = parseInt(cache.config.get('InputIncludingTaxUnitPrice')) === 2
    if (newCharge.unitPriceIncludeTax) {
      ChargeUtils.calculateTaxAmount(newCharge, multiply(newCharge.unitRate, newCharge.quantity), 0)
    } else {
      ChargeUtils.calculateTax(newCharge, multiply(newCharge.unitRate, newCharge.quantity), 0)
    }
    return newCharge
  }

  /**
   * 根据Ssc生成应收费用
   */
  static async convertQuotationAirDetailSscToCharge(quotationAirDetail, job) {
    const newCharge = await this.createAirDetailCharge(quotationAirDetail, job, 2)
    if (newCharge == null) return null
    newCharge.quantity = 1 // 数量
    newCharge.measure = ChargeMeasure.NML_CHARGE_MEASURE.code	// 普通价
    newCharge.unitRate = quotationAirDetail.ssc
    // 单价是否含税
    newCharge.unitPriceIncludeTax = parseInt(cache.config.get('InputIncludingTaxUnitPrice')) === 2
    if (newCharge.unitPriceIncludeTax) {
      ChargeUtils.calculateTaxAmount(newCharge, multiply(newCharge.unitRate, newCharge.quantity), 0)
    } else {
      ChargeUtils.calculateTax(newCharge, multiply(newCharge.unitRate, newCharge.quantity), 0)
    }
    return newCharge
  }

  /**
   * 根据Fsc生成应收费用
   */
  static async convertQuotationAirDetailFscToCharge(quotationAirDetail, job) {
    const newCharge = await this.createAirDetailCharge(quotationAirDetail, job, 1)
    if (newCharge == null) return null
    newCharge.quantity = 1	// 数量
    newCharge.measure = ChargeMeasure.NML_CHARGE_MEASURE.code	// 普通价
    newCharge.unitRate = quotationAirDetail.fsc
    // 单价是否含税
    newCharge.unitPriceIncludeTax = parseInt(cache.config.get('InputIncludingTaxUnitPrice')) === 2
    if (newCharge.unitPriceIncludeTax) {
      ChargeUtils.calculateTaxAmount(newCharge, multiply(newCharge.unitRate, newCharge.quantity), 0)
    } else {
      ChargeUtils.calculateTax(newCharge, multiply(newCharge.unitRate, newCharge.quantity), 0)
    }
    return newCharge
  }

  static replaceQuotationAirDetailNIL(quotationAirDetail) {
    if (String(quotationAirDetail.step).length > 10) {
      quotationAirDetail.step = 0.0
    }
    if (String(quotationAirDetail.p45).length > 10) {
      quotationAirDetail.p45 = 0.0
    }
    if (String(quotationAirDetail.p100).length > 10) {
      quotationAirDetail.p100 = 0.0
    }
    if (String(quotationAirDetail.p300).length > 10) {
      quotationAirDetail.p300 = 0.0
    }
    if (String(quotationAirDetail.p500).length > 10) {
      quotationAirDetail.p500 = 0.0
    }
    if (String(quotationAirDetail.p1000).length > 10) {
      quotationAirDetail.p1000 = 0.0
    }
  }

  /**
   * 生成应收费用
   */
  static async convertQuotationAirDetailToCharge(quotationAirDetail, job) {
    const newCharge = await this.createAirDetailCharge(quotationAirDetail, job)
    if (newCharge == null) return null
    this.replaceQuotationAirDetailNIL(quotationAirDetail)
    newCharge.quantity = job.chargeWeight	// 数量
    newCharge.quantity = newCharge.quantity < 1 ? 1 : newCharge.quantity
    newCharge.measure = ChargeMeasure.WGTX_CHARGE_MEASURE.code	// 单位
    if (newCharge.quantity > 0 && newCharge.quantity < 45) {
      newCharge.unitRate = quotationAirDetail.step
    } else if (newCharge.quantity >= 45 && newCharge.quantity < 100) {
      newCharge.unitRate = quotationAirDetail.p45
    } else if (newCharge.quantity >= 100 && newCharge.quantity < 300) {
      newCharge.unitRate = quotationAirDetail.p100
    } else if (newCharge.quantity >= 300 && newCharge.quantity < 500) {
      newCharge.unitRate = quotationAirDetail.p300
    } else if (newCharge.quantity >= 500 && newCharge.quantity < 1000) {
      newCharge.unitRate = quotationAirDetail.p500
    } else if (newCharge.quantity >= 1000) {
      newCharge.unitRate = quotationAirDetail.p1000
    }
    if ((newCharge.quantity * newCharge.unitRate) < quotationAirDetail.minCharge) {
      newCharge.unitRate = quotationAirDetail.minCharge
      newCharge.quantity = 1
      newCharge.measure = ChargeMeasure.NML_CHARGE_MEASURE.code	// 单位
    }
    // 单价是否含税
    newCharge.unitPriceIncludeTax = parseInt(cache.config.get('InputIncludingTaxUnitPrice')) === 2
    if (newCharge.unitPriceIncludeTax) {
      ChargeUtils.calculateTaxAmount(newCharge, multiply(newCharge.unitRate, newCharge.quantity), 0)
    } else {
      ChargeUtils.calculateTax(newCharge, multiply(newCharge.unitRate, newCharge.quantity), 0)
    }
    return newCharge
  }

  static async createAirDetailCharge(quotationAirDetail, job, chargeType = 0) {
    const charge = this.createChargeInfo(job)
    let chargeItem = null
    if (chargeType === 0) {
      chargeItem = await ChargeItem.getChargeItemByScaleCode(this.DEFAULT_SCALE_CODE_AIR)
    } else if (chargeType === 1 && String(quotationAirDetail.fsc).length <= 10 && quotationAirDetail.fsc !== 0) {
      chargeItem = await ChargeItem.getChargeItemByScaleCode(this.DEFAULT_SCALE_CODE_FSC)
    } else if (chargeType === 2 && String(quotationAirDetail.ssc).length <= 10 && quotationAirDetail.ssc !== 0) {
      chargeItem = await ChargeItem.getChargeItemByScaleCode(this.DEFAULT_SCALE_CODE_SSC)
    }
    if (chargeItem == null) {
      return null
    }
    charge.chargeItemId = chargeItem.itemId
    charge.accountId = job.customerId
    const localCurrency = RateUtil.localCurrencyByOfficeId(charge.branchId)
    charge.localCurrency = localCurrency
    charge.currency = localCurrency
    if (charge.accountId && charge.accountId > 0) {
      const company = await Company.getCompanyById(charge.accountId)
      if (company && (company.designationAgent || company.overseaAgent)) {
        charge.currency = 'USD'
      }
    }
    charge.exchangeRate = await RateUtil.getForeignExchangeRateByJob(localCurrency, quotationAirDetail.currency, job, charge.branchId)
    // HQ币种/汇率
    charge.hqCurrency = RateUtil.getHqCurrency(charge.branchId)
    charge.hqRate = await RateUtil.getForeignExchangeRateByJob(charge.hqCurrency, localCurrency, job, charge.branchId)
    charge.receivable = 1
    return charge
  }

  static createChargeInfo(job) {
    const charge = new Charge().fillDefaultValue().getData()
    charge.jobUuid = job.uuid
    charge.inputBy = $store.getters.userId
    charge.inputDate = new Date()
    charge.inputByDep = job.opDepartment <= 0 ? $store.getters.currentDeptId : job.opDepartment
    charge.branchId = job.opOffice <= 0 ? $store.getters.currentOfficeId : job.opOffice
    if (job.salesOffice && cache.config && parseInt(cache.config.get('CurrentOfficeCharge')) === 1) {
      charge.branchId = job.salesOffice
    }
    charge.accountId = job.customerId || 0
    return charge
  }

  /**
   * 根据价格明细创建费用
   */
  static async createChargeDetailCharge(quotationChargeDetail, job) {
    const charge = this.createChargeInfo(job)
    charge.chargeItemId = quotationChargeDetail.chargeId
    charge.currency = quotationChargeDetail.currency
    const localCurrency = RateUtil.localCurrencyByOfficeId(charge.branchId)
    charge.localCurrency = localCurrency
    charge.exchangeRate = await RateUtil.getForeignExchangeRateByJob(localCurrency, quotationChargeDetail.currency, job, charge.branchId)
    // HQ币种/汇率
    charge.hqCurrency = RateUtil.getHqCurrency(charge.branchId)
    charge.hqRate = await RateUtil.getForeignExchangeRateByJob(charge.hqCurrency, localCurrency, job, charge.branchId)
    charge.measure = quotationChargeDetail.unit
    return charge
  }

  /**
   * 生成应付费用
   * @param quotation
   * @param job
   */
  static async generatingPayableCharge(quotation, job) {
    if (quotation && quotation.priceUuid) {
      await queryPriceAirByKey({ key: quotation.priceUuid }).then(async res => {
        if (res.data && res.data.length > 0) {
          await this.generatingPayableChargeByPriceAir(res.data, job)
        }
      })
    }
  }

  /**
   * 根据空运运价生成应付费用
   */
  static async generatingPayableChargeByPriceAir(priceAirs, job) {
    const charges = []
    let charge = null
    for (const priceAir of priceAirs) {
      charge = await this.convertPriceAirToCharge(priceAir, job)
      if (charge != null) {
        charges.push(charge)
      }
      charge = await this.convertPriceFscToCharge(priceAir, job)
      if (charge != null) {
        charges.push(charge)
      }
      charge = await this.convertPriceSscToCharge(priceAir, job)
      if (charge != null) {
        charges.push(charge)
      }
      charge = await this.convertPriceTcToCharge(priceAir, job)
      if (charge != null) {
        charges.push(charge)
      }
    }
    if (charges && charges.length > 0) {
      if (!job.charges || job.charges.length === 0) {
        job.charges = []
      }
      job.charges = job.charges.concat(charges)
    }
  }

  static replacePriceNIL(priceAir) {
    if (String(priceAir.step).length > 10) {
      priceAir.step = 0.0
    }
    if (String(priceAir.p45).length > 10) {
      priceAir.p45 = 0.0
    }
    if (String(priceAir.p100).length > 10) {
      priceAir.p100 = 0.0
    }
    if (String(priceAir.p300).length > 10) {
      priceAir.p300 = 0.0
    }
    if (String(priceAir.p500).length > 10) {
      priceAir.p500 = 0.0
    }
    if (String(priceAir.p1000).length > 10) {
      priceAir.p1000 = 0.0
    }
  }
  /**
   * 验证平台代码是否存在
   */
  static async checkChargeItemScaleCode() {
    let chargeItem = await ChargeItem.getChargeItemByScaleCode(this.DEFAULT_SCALE_CODE_AIR)
    if (chargeItem == null) {
      Message.warning(lang.t('Pricing.NotMatchChargeItem', [lang.t('Pricing.AirFreight'), this.DEFAULT_SCALE_CODE_AIR]))
      return false
    }
    chargeItem = await ChargeItem.getChargeItemByScaleCode(this.DEFAULT_SCALE_CODE_FSC)
    if (chargeItem == null) {
      Message.warning(lang.t('Pricing.NotMatchChargeItem', [lang.t('Pricing.WarExpenses'), this.DEFAULT_SCALE_CODE_FSC]))
      return false
    }
    chargeItem = await ChargeItem.getChargeItemByScaleCode(this.DEFAULT_SCALE_CODE_SSC)
    if (chargeItem == null) {
      Message.warning(lang.t('Pricing.NotMatchChargeItem', [lang.t('Pricing.FuelSurcharge'), this.DEFAULT_SCALE_CODE_SSC]))
      return false
    }
    chargeItem = await ChargeItem.getChargeItemByScaleCode(this.DEFAULT_SCALE_CODE_TC)
    if (chargeItem == null) {
      Message.warning(lang.t('Pricing.NotMatchChargeItem', [lang.t('Pricing.WarehousingFee'), this.DEFAULT_SCALE_CODE_TC]))
      return false
    }
    return true
  }

  /**
   * 是否是NIL
   */
  static checkAmountNIL(value) {
    if (String(value).length <= 10) {
      return true
    }
    return false
  }

  /**
   * 运价比较
   * N <45
   * +45 >=45 and < 100
   * +100 >=100 and < 300
   * +300 >=300 and < 500
   * +500 >=500 and < 1000
   * +1000 >=1000
   * 按以上等级计算后，与M的金额比较取大值
   */
  static async convertPriceAirToCharge(priceAir, job) {
    const newCharge = await this.createCharge(priceAir, job)
    if (newCharge == null) return null
    this.replacePriceNIL(priceAir)
    newCharge.quantity = job.chargeWeight	// 数量
    newCharge.quantity = newCharge.quantity < 1 ? 1 : newCharge.quantity
    if (job.chargeWeight > 0 && job.chargeWeight < 45) {
      newCharge.unitRate = priceAir.step
    } else if (job.chargeWeight >= 45 && job.chargeWeight < 100) {
      newCharge.unitRate = priceAir.p45
    } else if (job.chargeWeight >= 100 && job.chargeWeight < 300) {
      newCharge.unitRate = priceAir.p100
    } else if (job.chargeWeight >= 300 && job.chargeWeight < 500) {
      newCharge.unitRate = priceAir.p300
    } else if (job.chargeWeight >= 500 && job.chargeWeight < 1000) {
      newCharge.unitRate = priceAir.p500
    } else if (job.chargeWeight >= 1000) {
      newCharge.unitRate = priceAir.p1000
    }
    if ((newCharge.quantity * newCharge.unitRate) < priceAir.minCharge) {
      newCharge.unitRate = priceAir.minCharge
      newCharge.quantity = 1
      newCharge.measure = ChargeMeasure.NML_CHARGE_MEASURE.code	// 单位
    }
    ChargeUtils.calculateTax(newCharge, newCharge.quantity * newCharge.unitRate, 0)
    return newCharge
  }

  /**
   * 根据Fsc生成费用
   */
  static async convertPriceFscToCharge(priceAir, job) {
    const newCharge = await this.createCharge(priceAir, job, 1)
    if (newCharge == null) return null
    newCharge.quantity = 1	// 数量
    newCharge.unitRate = priceAir.fsc
    ChargeUtils.calculateTax(newCharge, newCharge.quantity * newCharge.unitRate, 0)
    return newCharge
  }

  /**
   * 根据Ssc生成费用
   */
  static async convertPriceSscToCharge(priceAir, job) {
    const newCharge = await this.createCharge(priceAir, job, 2)
    if (newCharge == null) return null
    newCharge.quantity = 1	// 数量
    newCharge.unitRate = priceAir.ssc
    ChargeUtils.calculateTax(newCharge, newCharge.quantity * newCharge.unitRate, 0)
    return newCharge
  }

  /**
   * 根据TC生成费用
   */
  static async convertPriceTcToCharge(priceAir, job) {
    const newCharge = await this.createCharge(priceAir, job, 3)
    if (newCharge == null) return null
    newCharge.quantity = 1	// 数量
    newCharge.unitRate = priceAir.tc
    ChargeUtils.calculateTax(newCharge, newCharge.quantity * newCharge.unitRate, 0)
    return newCharge
  }

  /**
   * 基本信息
   */
  static async createCharge(priceAir, job, chargeType = 0) {
    const charge = new Charge().fillDefaultValue().getData()
    let chargeItem = null
    if (chargeType === 0) {
      chargeItem = await ChargeItem.getChargeItemByScaleCode(this.DEFAULT_SCALE_CODE_AIR)
    } else if (chargeType === 1 && this.checkAmountNIL(priceAir.fsc) && priceAir.fsc !== 0) {
      chargeItem = await ChargeItem.getChargeItemByScaleCode(this.DEFAULT_SCALE_CODE_FSC)
    } else if (chargeType === 2 && this.checkAmountNIL(priceAir.ssc) && priceAir.ssc !== 0) {
      chargeItem = await ChargeItem.getChargeItemByScaleCode(this.DEFAULT_SCALE_CODE_SSC)
    } else if (chargeType === 3 && this.checkAmountNIL(priceAir.tc) && priceAir.tc !== 0) {
      chargeItem = await ChargeItem.getChargeItemByScaleCode(this.DEFAULT_SCALE_CODE_TC)
    } else if (chargeType === 4 && this.checkAmountNIL(priceAir.salesAllowance) && priceAir.salesAllowance !== 0) {
      chargeItem = await ChargeItem.getChargeItemByScaleCode(this.DEFAULT_SCALE_CODE_BTA)
    }
    if (chargeItem == null) {
      return null
    }
    charge.chargeItemId = chargeItem.itemId
    charge.jobUuid = job.uuid
    charge.businessDate = job.businessDate
    charge.accountId = priceAir.carrier
    charge.currency = priceAir.currency
    charge.branchId = job.opOffice <= 0 ? $store.getters.currentOfficeId : job.opOffice
    if (job.salesOffice && cache.config && parseInt(cache.config.get('CurrentOfficeCharge')) === 1) {
      charge.branchId = job.salesOffice
    }
    const localCurrency = RateUtil.localCurrencyByOfficeId(charge.branchId)
    charge.localCurrency = localCurrency
    charge.exchangeRate = await RateUtil.getForeignExchangeRateByJob(localCurrency, priceAir.currency, job, charge.branchId)
    charge.inputBy = $store.getters.userId
    charge.inputDate = new Date()
    charge.inputByDep = job.opDepartment <= 0 ? $store.getters.currentDeptId : job.opDepartment
    // HQ币种/汇率
    charge.hqCurrency = RateUtil.getHqCurrency(charge.branchId)
    charge.hqRate = await RateUtil.getForeignExchangeRateByJob(charge.hqCurrency, localCurrency, job, charge.branchId)
    charge.receivable = 0
    charge.measure = chargeType === 0 ? ChargeMeasure.WGTX_CHARGE_MEASURE.code : ChargeMeasure.NML_CHARGE_MEASURE.code
    return charge
  }
}
